// uiarc: dialog (https://uiarc.dev/r/dialog.json) — Base UI + Tailwind'e çevrildi, Radix/CSS Modules yok.
// Perde ve gövde Base UI'nin `data-starting-style`/`data-ending-style` geçişleriyle açılıp kapanır
// (`backdrop-motion`, `dialog-motion`: index.css). Gövde 8px yükselip yayla oturur; çıkış kısa ve sessizdir.
// Kapanırken tıklama içinden geçer (tetikleyici yeniden açabilir). Başlık/açıklama metni açıkken değişirse
// `SwapText` ile yer değiştirir. uiarc'ın `title`/`description` prop'lu tek parça API'si yerine proje bileşimi korundu:
// `DialogHeader` (başlık satırı + alt çizgi) → `DialogBody` (içerik) → `DialogFooter` (eylemler).
import * as React from 'react'
import { Dialog as DialogPrimitive } from '@base-ui/react/dialog'
import { cn } from '@/lib/utils'

import { Button } from '@/components/ui/button'
import { OverlayClose } from '@/components/ui/overlay-close'
import { SwapContent } from '@/components/ui/swap-text'
import { messages } from '@/lib/messages'

function Dialog({ ...props }: DialogPrimitive.Root.Props) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />
}

function DialogTrigger({ ...props }: DialogPrimitive.Trigger.Props) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

function DialogPortal({ ...props }: DialogPrimitive.Portal.Props) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
}

function DialogClose({ ...props }: DialogPrimitive.Close.Props) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />
}

function DialogOverlay({ className, ...props }: DialogPrimitive.Backdrop.Props) {
  return (
    <DialogPrimitive.Backdrop
      data-slot="dialog-overlay"
      className={cn(
        'backdrop-motion fixed inset-0 z-50 bg-[oklch(10%_0_0/0.46)] backdrop-blur-[7px]',
        className,
      )}
      {...props}
    />
  )
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  ...props
}: DialogPrimitive.Popup.Props & {
  showCloseButton?: boolean
}) {
  return (
    <DialogPortal>
      <DialogOverlay />
      {/* Kenar boşlukları `auto`: yerel modal gibi ortalanır, `transform` giriş yayına kalır. */}
      <DialogPrimitive.Popup
        data-slot="dialog-content"
        className={cn(
          'dialog-motion border-border bg-surface-raised text-foreground shadow-floating rounded-surface fixed inset-0 z-51 m-auto h-fit max-h-[calc(100dvh-2rem)] w-full max-w-[min(calc(100vw-2rem),440px)] overflow-auto border outline-none',
          className,
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          // Başlığın ilk satırına ortalı: (satır yüksekliği - düğme) / 2.
          <OverlayClose className="absolute top-[calc(1.5rem+(1.125rem*1.4-2rem)/2)] right-6" />
        )}
      </DialogPrimitive.Popup>
    </DialogPortal>
  )
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-header"
      className={cn(
        'border-border flex flex-col gap-2 border-b p-5 pr-[4.75rem] text-left sm:p-6 sm:pr-[4.75rem]',
        className,
      )}
      {...props}
    />
  )
}

/** uiarc gövde alanı: 24px iç boşluk, 14px metin. */
function DialogBody({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-body"
      className={cn('leading-body p-5 text-sm sm:p-6', className)}
      {...props}
    />
  )
}

function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  showCloseButton?: boolean
}) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        'border-border flex flex-col-reverse gap-2 border-t px-5 py-4 sm:flex-row sm:justify-end sm:px-6',
        className,
      )}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close render={<Button variant="secondary" />}>
          {messages.common.close}
        </DialogPrimitive.Close>
      )}
    </div>
  )
}

function DialogTitle({ className, children, ...props }: DialogPrimitive.Title.Props) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn(
        'leading-body relative m-0 text-lg font-medium tracking-[-0.01em] [overflow-wrap:anywhere]',
        className,
      )}
      {...props}
    >
      <SwapContent>{children as React.ReactNode}</SwapContent>
    </DialogPrimitive.Title>
  )
}

function DialogDescription({ className, children, ...props }: DialogPrimitive.Description.Props) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn(
        'text-text-secondary leading-body *:[a]:hover:text-foreground relative m-0 text-sm [overflow-wrap:anywhere] *:[a]:underline *:[a]:underline-offset-3',
        className,
      )}
      {...props}
    >
      <SwapContent>{children as React.ReactNode}</SwapContent>
    </DialogPrimitive.Description>
  )
}

export {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
}
